import React from 'react'

import { CountryFlag, NumberString, PriceString, ProfitString } from '@fourthwall/components'

import { TimezonedDateString } from '../../../../../../../../Timezone/components/TimezonedDateString'
import type { AnalyticsPrecision } from '../../../../types'
import { AnalyticsDataLabel } from '../../../AnalyticsDataLabel/AnalyticsDataLabel'
import { AnalyticsPaymentMethodLabel } from '../../../AnalyticsPaymentMethod/AnalyticsPaymentMethodLabel'
import type { AnalyticsPaymentMethodLabelType } from '../../../AnalyticsPaymentMethod/AnalyticsPaymentMethodLabel.types'
import { AnalyticsProductLabel } from '../../../AnalyticsProductLabel/AnalyticsProductLabel'
import { AnalyticsSocialPlatformLabel } from '../../../AnalyticsSocialPlatform/AnalyticsSocialPlatformLabel'
import type { AnalyticsSocialPlatformLabelType } from '../../../AnalyticsSocialPlatform/AnalyticsSocialPlatformLabel.types'
import type {
  AnalyticsTableCellDecorator,
  AnalyticsTableCellType,
} from '../../AnalyticsTable.types'
import type { Value } from '../AnalyticsTableCell/AnalyticsTableCell.types'

const DATE_FORMATS: Record<AnalyticsPrecision, string> = {
  hour: 'MMM D, YYYY h:mm A',
  day: 'MMM D, YYYY',
  week: '[Week] w, YYYY',
  month: 'MMM, YYYY',
  quarter: '[Q]Q, YYYY',
  year: 'YYYY',
} as const

type AnalyticsTableCellValueFormat = {
  value: Value | undefined
  type: AnalyticsTableCellType
  columnIndex?: number
  decorator?: AnalyticsTableCellDecorator
  precision?: AnalyticsPrecision
  metadata?: Record<string, string | number>
  isSummary?: boolean
}

export const AnalyticsTableCellValueFormat = ({
  value,
  type,
  decorator,
  precision,
  metadata,
  isSummary,
  columnIndex,
}: AnalyticsTableCellValueFormat) => {
  if (value === null || value === undefined) {
    if (isSummary) {
      return columnIndex === 0 ? 'Total' : 'N/A'
    }

    return '-'
  }

  // Date
  if (type === 'date') {
    // Date range
    if (typeof value === 'object' && 'from' in value && 'to' in value) {
      return (
        <>
          <TimezonedDateString date={value.from} format="MMM D, YYYY" /> -{' '}
          <TimezonedDateString date={value.to} format="MMM D, YYYY" />
        </>
      )
    }

    // Single date
    return (
      <TimezonedDateString
        date={value as string}
        format={DATE_FORMATS[precision || 'day']}
        withTimezoneAbbreviation={false}
      />
    )
  }

  // Money
  if (type === 'money' && typeof value === 'object' && 'value' in value && 'currency' in value) {
    if (decorator === 'change') {
      return <ProfitString value={value.value} />
    }

    return <PriceString value={value.value} currency={value.currency} />
  }

  // Number
  if (type === 'number') {
    if (value === '') {
      return 0
    }

    return <NumberString value={value as number} />
  }

  // Boolean
  if (type === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // Percentage
  if (type === 'percentage') {
    if (value === '') {
      return '0%'
    }

    // Format to max 2 decimal places, removing trailing zeros
    const formattedValue = (value as number).toFixed(2).replace(/\.?0+$/, '')

    return `${formattedValue}%`
  }

  if (type === 'string') {
    if (value === '') {
      return '-'
    }

    if (decorator === 'country') {
      return <CountryFlag countryCode={value as string} size={20} />
    }

    if (decorator === 'payment_method') {
      return <AnalyticsPaymentMethodLabel type={value as AnalyticsPaymentMethodLabelType} />
    }

    if (decorator === 'social_platform') {
      return <AnalyticsSocialPlatformLabel type={value as AnalyticsSocialPlatformLabelType} />
    }

    if (decorator === 'offer') {
      return (
        <AnalyticsProductLabel
          name={value as string}
          status={metadata?.status as string | undefined}
          productId={metadata?.offerId as string | undefined}
        />
      )
    }

    return <AnalyticsDataLabel label={value as string} />
  }

  return value.toLocaleString()
}
